import React from 'react';
import { fabric } from 'fabric';
import { useTranslation } from 'react-i18next';
import {
    AlignCenter,
    AlignHorizontalDistributeCenter,
    AlignHorizontalJustifyCenter,
    AlignHorizontalJustifyEnd,
    AlignHorizontalJustifyStart,
    AlignJustify,
    AlignLeft,
    AlignRight,
    AlignVerticalDistributeCenter,
    AlignVerticalJustifyCenter,
    AlignVerticalJustifyEnd,
    AlignVerticalJustifyStart,
    ArrowDownToLine,
    ArrowUpToLine,
    CaseSensitive,
    ChevronDown,
    ChevronUp,
    Copy,
    Crop,
    FlipHorizontal2,
    FlipVertical2,
    Group,
    Italic,
    Lock,
    LockOpen,
    Maximize,
    Strikethrough,
    Trash2,
    Underline,
    Ungroup,
    Wallpaper,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

import { ARTBOARD_PRESETS, ratioLabel } from './artboard';
import { parseColorWithAlpha, rgba } from './colors';
import { isImageObject, isTextObject, primaryFamily } from './fabric-setup';
import { FontPicker } from './FontPicker';
import { fontHasItalic, fontWeightsFor } from './fonts';
import { groupColor } from './objects';
import { rebuildPolygon } from './shapes';
import type { BrandInfo, MaskKind } from './types';
import { readFilters, DesignEditorApi } from './useDesignEditor';
import { ColorField, IconSegment, NumberField, PanelSection, SliderField, ToggleChip } from './ui';

/** Ratio glyph for a preset tile. */
const RatioBox: React.FC<{ w: number; h: number }> = ({ w, h }) => {
    const k = 22 / Math.max(w, h);
    return <span className="inline-block rounded-[3px] border-[1.5px] border-current" style={{ width: w * k, height: h * k }} />;
};

export const Inspector: React.FC<{
    ed: DesignEditorApi;
    brand: BrandInfo;
    swatches: string[];
    onOpenImages: () => void;
    originalSize: { w: number; h: number } | null;
}> = ({ ed, brand, swatches, onOpenImages, originalSize }) => {
    const { t } = useTranslation();
    const o = ed.active;
    const sel = ed.selection;

    if (!ed.ready) return null;

    // ── Crop mode ──
    if (ed.cropping) {
        const ratios: Array<{ key: string; r: number | null }> = [
            { key: 'FREE', r: null }, { key: '1_1', r: 1 }, { key: '4_5', r: 4 / 5 }, { key: '3_2', r: 3 / 2 }, { key: '16_9', r: 16 / 9 }, { key: '9_16', r: 9 / 16 },
        ];
        return (
            <div className="flex flex-col">
                <PanelSection title={t('DESIGN_EDITOR.CROP')}>
                    <p className="text-xs leading-relaxed text-muted-foreground">{t('DESIGN_EDITOR.CROP_HINT')}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                        {ratios.map((r) => (
                            <ToggleChip key={r.key} onClick={() => ed.setCropAspect(r.r)}>{t(`DESIGN_EDITOR.RATIO_${r.key}`)}</ToggleChip>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={ed.cancelCrop} className="h-9 flex-1 cursor-pointer rounded-lg border bg-background text-xs text-foreground hover:bg-accent">
                            {t('DESIGN_EDITOR.CANCEL')}
                        </button>
                        <button type="button" onClick={ed.applyCrop} className="h-9 flex-1 cursor-pointer rounded-lg bg-foreground text-xs font-medium text-background hover:opacity-90">
                            {t('DESIGN_EDITOR.CROP_APPLY')}
                        </button>
                    </div>
                </PanelSection>
            </div>
        );
    }

    // ── Nothing selected: the artboard ──
    if (!o || sel.length === 0) {
        const c = ed.canvas;
        const bgLayer = c?.getObjects().find((x) => (x as any).csRole === 'background') as fabric.Object | undefined;
        const presets = [...ARTBOARD_PRESETS];
        return (
            <div className="flex flex-col">
                <PanelSection title={t('DESIGN_EDITOR.ARTBOARD')}>
                    <div className="grid grid-cols-3 gap-1.5">
                        {originalSize && (
                            <PresetTile
                                active={ed.artboard.w === originalSize.w && ed.artboard.h === originalSize.h}
                                label={t('DESIGN_EDITOR.SIZE_ORIGINAL')}
                                ratio={ratioLabel(originalSize)}
                                w={originalSize.w}
                                h={originalSize.h}
                                onClick={() => ed.setArtboard(originalSize)}
                            />
                        )}
                        {presets.map((p) => (
                            <PresetTile
                                key={p.id}
                                active={ed.artboard.w === p.w && ed.artboard.h === p.h}
                                label={t(p.labelKey)}
                                ratio={p.ratio}
                                w={p.w}
                                h={p.h}
                                onClick={() => ed.setArtboard({ w: p.w, h: p.h })}
                            />
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                        <NumberField label="W" min={100} max={4096} value={ed.artboard.w} onCommit={(v) => ed.setArtboard({ w: v, h: ed.artboard.h })} title={t('DESIGN_EDITOR.WIDTH')} />
                        <NumberField label="H" min={100} max={4096} value={ed.artboard.h} onCommit={(v) => ed.setArtboard({ w: ed.artboard.w, h: v })} title={t('DESIGN_EDITOR.HEIGHT')} />
                    </div>
                    <ColorField
                        label={t('DESIGN_EDITOR.BACKGROUND_COLOR')}
                        title={t('DESIGN_EDITOR.BACKGROUND_COLOR')}
                        value={(c?.backgroundColor as any) || '#FFFFFF'}
                        onChange={(p) => ed.setArtboardColor(p as any)}
                        swatches={swatches}
                    />
                </PanelSection>
                {bgLayer && isImageObject(bgLayer) && (
                    <PanelSection title={t('DESIGN_EDITOR.BACKGROUND_IMAGE')}>
                        <p className="text-xs leading-relaxed text-muted-foreground">{t('DESIGN_EDITOR.BACKGROUND_IMAGE_HINT')}</p>
                        <div className="grid grid-cols-2 gap-1.5">
                            <ToggleChip onClick={() => ed.fitToArtboard(bgLayer as fabric.Image, 'cover')}><Maximize />{t('DESIGN_EDITOR.FIT_COVER')}</ToggleChip>
                            <ToggleChip onClick={onOpenImages}><Wallpaper />{t('DESIGN_EDITOR.REPLACE')}</ToggleChip>
                            <ToggleChip onClick={() => { ed.toggleLock(bgLayer); ed.select(bgLayer); }}><LockOpen />{t('DESIGN_EDITOR.UNLOCK_EDIT')}</ToggleChip>
                            <ToggleChip onClick={() => { ed.toggleLock(bgLayer); ed.select(bgLayer); ed.deleteSelection(); }} className="text-destructive hover:text-destructive"><Trash2 />{t('DESIGN_EDITOR.REMOVE')}</ToggleChip>
                        </div>
                    </PanelSection>
                )}
                <div className="px-4 py-4 text-xs leading-relaxed text-muted-foreground">{t('DESIGN_EDITOR.INSPECTOR_EMPTY')}</div>
            </div>
        );
    }

    const multi = sel.length > 1;
    const alignButtons = (
        <div className="grid grid-cols-6 gap-1">
            {([
                ['left', AlignHorizontalJustifyStart, 'ALIGN_LEFT'],
                ['hcenter', AlignHorizontalJustifyCenter, 'ALIGN_HCENTER'],
                ['right', AlignHorizontalJustifyEnd, 'ALIGN_RIGHT'],
                ['top', AlignVerticalJustifyStart, 'ALIGN_TOP'],
                ['vcenter', AlignVerticalJustifyCenter, 'ALIGN_VCENTER'],
                ['bottom', AlignVerticalJustifyEnd, 'ALIGN_BOTTOM'],
            ] as const).map(([mode, Icon, key]) => (
                <button
                    key={mode}
                    type="button"
                    title={t(`DESIGN_EDITOR.${key}`)}
                    aria-label={t(`DESIGN_EDITOR.${key}`)}
                    onClick={() => ed.align(mode)}
                    className="flex h-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-4"
                >
                    <Icon />
                </button>
            ))}
        </div>
    );

    const orderRow = (
        <div className="grid grid-cols-4 gap-1">
            {([
                ['front', ArrowUpToLine, 'BRING_FRONT'],
                ['forward', ChevronUp, 'BRING_FORWARD'],
                ['backward', ChevronDown, 'SEND_BACKWARD'],
                ['back', ArrowDownToLine, 'SEND_BACK'],
            ] as const).map(([dir, Icon, key]) => (
                <button
                    key={dir}
                    type="button"
                    title={t(`DESIGN_EDITOR.${key}`)}
                    aria-label={t(`DESIGN_EDITOR.${key}`)}
                    onClick={() => ed.order(dir)}
                    className="flex h-8 cursor-pointer items-center justify-center rounded-lg border text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground [&_svg]:size-4"
                >
                    <Icon />
                </button>
            ))}
        </div>
    );

    const opacity = (
        <SliderField
            label={t('DESIGN_EDITOR.OPACITY')}
            min={0} max={1} step={0.01}
            value={o.opacity ?? 1}
            onChange={(v) => ed.update({ opacity: v })}
            suffix="%"
            displayValue={Math.round((o.opacity ?? 1) * 100)}
        />
    );

    if (multi) {
        return (
            <div className="flex flex-col">
                <PanelSection title={t('DESIGN_EDITOR.SELECTION_COUNT', { count: sel.length })}>
                    <div className="text-[11px] text-muted-foreground">{t('DESIGN_EDITOR.ALIGN_TO_SELECTION')}</div>
                    {alignButtons}
                    <div className="grid grid-cols-2 gap-1.5">
                        <ToggleChip onClick={() => ed.distribute('h')} disabled={sel.length < 3}><AlignHorizontalDistributeCenter />{t('DESIGN_EDITOR.DISTRIBUTE_H')}</ToggleChip>
                        <ToggleChip onClick={() => ed.distribute('v')} disabled={sel.length < 3}><AlignVerticalDistributeCenter />{t('DESIGN_EDITOR.DISTRIBUTE_V')}</ToggleChip>
                    </div>
                    <ToggleChip onClick={ed.group}><Group />{t('DESIGN_EDITOR.GROUP')}</ToggleChip>
                </PanelSection>
                <PanelSection title={t('DESIGN_EDITOR.EFFECTS')}>{opacity}</PanelSection>
                <PanelSection title={t('DESIGN_EDITOR.ARRANGE')}>{orderRow}</PanelSection>
            </div>
        );
    }

    const any = o as any;
    const isText = isTextObject(o);
    const isImage = isImageObject(o);
    const isIconGroup = o.type === 'group' && (any.csRole === 'icon' || any.csRole === 'divider');
    const isLineLike = o.type === 'line' || any.csShape === 'arrow';
    const isShape = !isText && !isImage && o.type !== 'group';
    const bounds = o.getBoundingRect(true, true);

    return (
        <div className="flex flex-col">
            <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
                <span className="text-xs font-medium text-foreground">{t(isText ? 'DESIGN_EDITOR.KIND_TEXT' : isImage ? 'DESIGN_EDITOR.KIND_IMAGE' : isIconGroup ? 'DESIGN_EDITOR.KIND_ICON' : o.type === 'group' ? 'DESIGN_EDITOR.KIND_GROUP' : 'DESIGN_EDITOR.KIND_SHAPE')}</span>
                <div className="flex items-center gap-0.5">
                    {o.type === 'group' && !isIconGroup && (
                        <button type="button" onClick={ed.ungroup} title={t('DESIGN_EDITOR.UNGROUP')} className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-3.5"><Ungroup /></button>
                    )}
                    <button type="button" onClick={ed.duplicate} title={t('DESIGN_EDITOR.DUPLICATE')} className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-3.5"><Copy /></button>
                    <button type="button" onClick={() => ed.toggleLock()} title={t('DESIGN_EDITOR.LOCK')} className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground [&_svg]:size-3.5"><Lock /></button>
                    <button type="button" onClick={ed.deleteSelection} title={t('DESIGN_EDITOR.DELETE')} className="flex size-7 cursor-pointer items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive [&_svg]:size-3.5"><Trash2 /></button>
                </div>
            </div>

            <PanelSection title={t('DESIGN_EDITOR.LAYOUT')}>
                {alignButtons}
                <div className="grid grid-cols-2 gap-1.5">
                    <NumberField label="X" value={bounds.left} onCommit={(v) => ed.setGeometry({ x: v })} />
                    <NumberField label="Y" value={bounds.top} onCommit={(v) => ed.setGeometry({ y: v })} />
                    <NumberField label="W" min={1} value={o.getScaledWidth()} onCommit={(v) => ed.setGeometry({ w: v })} />
                    <NumberField label="H" min={1} value={o.getScaledHeight()} onCommit={(v) => ed.setGeometry({ h: v })} className={isText ? 'pointer-events-none opacity-50' : undefined} />
                    <NumberField label="°" min={-360} max={360} value={o.angle || 0} onCommit={(v) => ed.setGeometry({ angle: v })} title={t('DESIGN_EDITOR.ROTATION')} />
                </div>
            </PanelSection>

            {isText && <TextSection ed={ed} tb={o as fabric.Textbox} brand={brand} swatches={swatches} />}

            {isShape && (
                <PanelSection title={t('DESIGN_EDITOR.FILL_AND_STROKE')}>
                    {!isLineLike && any.csShape !== 'frame' && (
                        <ColorField label={t('DESIGN_EDITOR.FILL')} title={t('DESIGN_EDITOR.FILL')} value={o.fill as any} onChange={(p) => ed.update({ fill: p, csColorRole: undefined })} swatches={swatches} allowGradient />
                    )}
                    <ColorField label={t('DESIGN_EDITOR.STROKE')} title={t('DESIGN_EDITOR.STROKE')} value={(o.stroke as any) || '#1A1917'} onChange={(p) => ed.update({ stroke: p, strokeWidth: o.strokeWidth || 2 })} swatches={swatches} />
                    <SliderField label={t('DESIGN_EDITOR.STROKE_WIDTH')} min={0} max={40} step={0.5} precision={1} suffix=" px" value={o.stroke ? o.strokeWidth || 0 : 0} onChange={(v) => ed.update({ strokeWidth: v, stroke: o.stroke || '#1A1917', strokeUniform: true })} />
                    <IconSegment
                        value={!o.strokeDashArray?.length ? 'solid' : (o.strokeDashArray[0] || 0) <= (o.strokeWidth || 1) * 1.5 ? 'dotted' : 'dashed'}
                        onChange={(v) => {
                            const w = Math.max(1, o.strokeWidth || 2);
                            ed.update({
                                strokeDashArray: v === 'solid' ? null : v === 'dashed' ? [w * 4, w * 3] : [w * 0.01, w * 2.2],
                                strokeLineCap: v === 'dotted' ? 'round' : o.type === 'line' ? 'round' : 'butt',
                            }, { history: 'now' });
                        }}
                        options={[
                            { value: 'solid', label: t('DESIGN_EDITOR.DASH_SOLID'), icon: <span className="block h-[2px] w-5 bg-current" /> },
                            { value: 'dashed', label: t('DESIGN_EDITOR.DASH_DASHED'), icon: <span className="block h-[2px] w-5 bg-[repeating-linear-gradient(90deg,currentColor_0_5px,transparent_5px_8px)]" /> },
                            { value: 'dotted', label: t('DESIGN_EDITOR.DASH_DOTTED'), icon: <span className="block h-[2px] w-5 bg-[repeating-linear-gradient(90deg,currentColor_0_2px,transparent_2px_5px)]" /> },
                        ]}
                    />
                    {o.type === 'rect' && (
                        <SliderField
                            label={t('DESIGN_EDITOR.CORNER_RADIUS')}
                            min={0}
                            max={Math.max(1, Math.floor(Math.min(o.width || 0, o.height || 0) / 2))}
                            step={1}
                            suffix=" px"
                            value={(o as fabric.Rect).rx || 0}
                            onChange={(v) => ed.update({ rx: v, ry: v })}
                        />
                    )}
                    {o.type === 'polygon' && (any.csShape === 'polygon' || any.csShape === 'star' || any.csShape === 'burst') && (
                        <>
                            <SliderField
                                label={any.csShape === 'polygon' ? t('DESIGN_EDITOR.SIDES') : t('DESIGN_EDITOR.POINTS')}
                                min={3} max={any.csShape === 'burst' ? 24 : 12} step={1}
                                value={any.csSides || 5}
                                onChange={(v) => { if (Math.round(v) !== any.csSides) ed.replaceObject(o, rebuildPolygon(o as fabric.Polygon, { sides: Math.round(v) })); }}
                            />
                            {any.csShape !== 'polygon' && (
                                <SliderField
                                    label={t('DESIGN_EDITOR.INNER_RADIUS')}
                                    min={0.2} max={0.95} step={0.01} precision={2}
                                    value={any.csInner || 0.45}
                                    onChange={(v) => ed.replaceObject(o, rebuildPolygon(o as fabric.Polygon, { inner: v }))}
                                />
                            )}
                        </>
                    )}
                </PanelSection>
            )}

            {isIconGroup && (
                <PanelSection title={t('DESIGN_EDITOR.COLOR')}>
                    <ColorField label={t('DESIGN_EDITOR.COLOR')} title={t('DESIGN_EDITOR.COLOR')} value={groupColor(o)} onChange={(p) => typeof p === 'string' && ed.setColor(p)} swatches={swatches} />
                </PanelSection>
            )}

            {isImage && <ImageSection ed={ed} img={o as fabric.Image} onOpenImages={onOpenImages} />}

            <PanelSection title={t('DESIGN_EDITOR.EFFECTS')}>
                {opacity}
                <ShadowControls ed={ed} obj={o} swatches={swatches} />
            </PanelSection>

            <PanelSection title={t('DESIGN_EDITOR.ARRANGE')}>
                {orderRow}
            </PanelSection>
        </div>
    );
};

const PresetTile: React.FC<{ active: boolean; label: string; ratio: string; w: number; h: number; onClick: () => void }> = ({ active, label, ratio, w, h, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        title={`${label} · ${w} × ${h}`}
        className={cn(
            'flex cursor-pointer flex-col items-center gap-1 rounded-xl border bg-background px-1 py-2 text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground',
            active && 'border-foreground text-foreground ring-[0.5px] ring-foreground',
        )}
    >
        <span className="flex h-6 items-center"><RatioBox w={w} h={h} /></span>
        <span className="line-clamp-1 text-[10.5px] leading-tight text-foreground">{label}</span>
        <span className="text-[10px] tabular-nums">{ratio}</span>
    </button>
);

const TextSection: React.FC<{ ed: DesignEditorApi; tb: fabric.Textbox; brand: BrandInfo; swatches: string[] }> = ({ ed, tb, brand, swatches }) => {
    const { t } = useTranslation();
    const any = tb as any;
    const family = primaryFamily(tb.fontFamily);
    const weights = fontWeightsFor(family);
    const weight = Number(tb.fontWeight) || 400;
    const text = tb.text || '';
    const isUpper = text.length > 0 && text === text.toUpperCase() && text !== text.toLowerCase();
    const plate = !!tb.backgroundColor && (any.bgPadX || any.bgPadY);
    const sizeOf = tb.fontSize || 24;
    const textOnly = (o: fabric.Object) => isTextObject(o);
    // Fabric gives every text a strokeWidth of 1 with no stroke colour: no outline.
    const outlineWidth = tb.stroke ? tb.strokeWidth || 0 : 0;

    return (
        <>
            <PanelSection title={t('DESIGN_EDITOR.TYPOGRAPHY')}>
                <FontPicker value={family} onChange={(f) => { void ed.setFont(f); }} brandFont={brand.font} className="w-full" />
                <div className="grid grid-cols-[1fr_88px] gap-1.5">
                    <Select value={String(weights.includes(weight) ? weight : weights[0])} onValueChange={(v) => { void ed.setTextStyle({ fontWeight: Number(v) }); }}>
                        <SelectTrigger className="h-8 w-full rounded-lg border-transparent bg-foreground/[0.045] text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {weights.map((w) => (
                                <SelectItem key={w} value={String(w)}>{t(`DESIGN_EDITOR.WEIGHT_${w}`)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <NumberField label={t('DESIGN_EDITOR.SIZE_SHORT')} min={4} max={1000} value={sizeOf} onCommit={(v) => ed.update({ fontSize: v }, { filter: textOnly, history: 'now' })} />
                </div>
                <IconSegment
                    value={(tb.textAlign as any) || 'left'}
                    onChange={(v) => ed.update({ textAlign: v }, { filter: textOnly, history: 'now' })}
                    options={[
                        { value: 'left', label: t('DESIGN_EDITOR.TEXT_LEFT'), icon: <AlignLeft /> },
                        { value: 'center', label: t('DESIGN_EDITOR.TEXT_CENTER'), icon: <AlignCenter /> },
                        { value: 'right', label: t('DESIGN_EDITOR.TEXT_RIGHT'), icon: <AlignRight /> },
                        { value: 'justify', label: t('DESIGN_EDITOR.TEXT_JUSTIFY'), icon: <AlignJustify /> },
                    ]}
                />
                <div className="grid grid-cols-4 gap-1">
                    <ToggleChip title={t('DESIGN_EDITOR.ITALIC')} active={tb.fontStyle === 'italic'} disabled={!fontHasItalic(family) && tb.fontStyle !== 'italic'} onClick={() => { void ed.setTextStyle({ fontStyle: tb.fontStyle === 'italic' ? 'normal' : 'italic' }); }}><Italic /></ToggleChip>
                    <ToggleChip title={t('DESIGN_EDITOR.UNDERLINE')} active={!!tb.underline} onClick={() => ed.update({ underline: !tb.underline }, { filter: textOnly, history: 'now' })}><Underline /></ToggleChip>
                    <ToggleChip title={t('DESIGN_EDITOR.STRIKE')} active={!!tb.linethrough} onClick={() => ed.update({ linethrough: !tb.linethrough }, { filter: textOnly, history: 'now' })}><Strikethrough /></ToggleChip>
                    <ToggleChip title={t('DESIGN_EDITOR.UPPERCASE')} active={isUpper} onClick={() => ed.forEachSelected((o) => { if (isTextObject(o)) { const s = o.text || ''; o.set({ text: isUpper ? s.charAt(0) + s.slice(1).toLowerCase() : s.toUpperCase() }); } })}><CaseSensitive /></ToggleChip>
                </div>
                <SliderField label={t('DESIGN_EDITOR.LETTER_SPACING')} min={-100} max={800} step={10} value={tb.charSpacing || 0} onChange={(v) => ed.update({ charSpacing: v }, { filter: textOnly })} />
                <SliderField label={t('DESIGN_EDITOR.LINE_HEIGHT')} min={0.7} max={2.4} step={0.02} precision={2} value={tb.lineHeight || 1.16} onChange={(v) => ed.update({ lineHeight: v }, { filter: textOnly })} />
                <ColorField label={t('DESIGN_EDITOR.TEXT_COLOR')} title={t('DESIGN_EDITOR.TEXT_COLOR')} value={tb.fill as any} onChange={(p) => ed.update({ fill: p, csColorRole: undefined, csColorSet: true }, { filter: textOnly })} swatches={swatches} allowGradient />
            </PanelSection>

            <PanelSection title={t('DESIGN_EDITOR.TEXT_OUTLINE')} defaultOpen={outlineWidth > 0}>
                <SliderField label={t('DESIGN_EDITOR.STROKE_WIDTH')} min={0} max={Math.max(4, sizeOf * 0.12)} step={0.25} precision={2} suffix=" px" value={outlineWidth} onChange={(v) => ed.update(v > 0 ? { strokeWidth: v, stroke: tb.stroke || '#1A1917', paintFirst: 'stroke', strokeLineJoin: 'round' } : { stroke: null, strokeWidth: 1 }, { filter: textOnly })} />
                {outlineWidth > 0 && (
                    <ColorField label={t('DESIGN_EDITOR.STROKE')} title={t('DESIGN_EDITOR.STROKE')} value={(tb.stroke as any) || '#1A1917'} onChange={(p) => ed.update({ stroke: p }, { filter: textOnly })} swatches={swatches} />
                )}
            </PanelSection>

            <PanelSection
                title={t('DESIGN_EDITOR.TEXT_BACKGROUND')}
                defaultOpen={!!plate}
                action={<Switch checked={!!plate} onCheckedChange={(v) => ed.setPlate(v)} aria-label={t('DESIGN_EDITOR.TEXT_BACKGROUND')} />}
            >
                {plate ? (
                    <>
                        <ColorField label={t('DESIGN_EDITOR.PLATE_COLOR')} title={t('DESIGN_EDITOR.PLATE_COLOR')} value={tb.backgroundColor as any} onChange={(p) => ed.update({ backgroundColor: p }, { filter: textOnly })} swatches={swatches} />
                        <SliderField label={t('DESIGN_EDITOR.PADDING_X')} min={0} max={sizeOf * 3} step={1} suffix=" px" value={any.bgPadX || 0} onChange={(v) => ed.update({ bgPadX: v }, { filter: textOnly })} />
                        <SliderField label={t('DESIGN_EDITOR.PADDING_Y')} min={0} max={sizeOf * 2} step={1} suffix=" px" value={any.bgPadY || 0} onChange={(v) => ed.update({ bgPadY: v }, { filter: textOnly })} />
                        <SliderField label={t('DESIGN_EDITOR.CORNER_RADIUS')} min={0} max={sizeOf * 2} step={1} suffix=" px" value={any.bgRadius || 0} onChange={(v) => ed.update({ bgRadius: v }, { filter: textOnly })} />
                    </>
                ) : (
                    <p className="text-xs leading-relaxed text-muted-foreground">{t('DESIGN_EDITOR.TEXT_BACKGROUND_HINT')}</p>
                )}
            </PanelSection>
        </>
    );
};

const ImageSection: React.FC<{ ed: DesignEditorApi; img: fabric.Image; onOpenImages: () => void }> = ({ ed, img, onOpenImages }) => {
    const { t } = useTranslation();
    const any = img as any;
    const mask: MaskKind = any.csMask || 'none';
    const f = readFilters(img);
    const u = Math.min(ed.artboard.w, ed.artboard.h);
    return (
        <>
            <PanelSection title={t('DESIGN_EDITOR.IMAGE')}>
                <div className="grid grid-cols-2 gap-1.5">
                    <ToggleChip onClick={ed.startCrop} disabled={Math.abs(img.angle || 0) > 0.01} title={Math.abs(img.angle || 0) > 0.01 ? t('DESIGN_EDITOR.CROP_ROTATED') : t('DESIGN_EDITOR.CROP')}><Crop />{t('DESIGN_EDITOR.CROP')}</ToggleChip>
                    <ToggleChip onClick={onOpenImages} title={t('DESIGN_EDITOR.REPLACE')}><Wallpaper />{t('DESIGN_EDITOR.REPLACE')}</ToggleChip>
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                    <ToggleChip onClick={() => ed.flip('h')} active={!!img.flipX} title={t('DESIGN_EDITOR.FLIP_H')}><FlipHorizontal2 /></ToggleChip>
                    <ToggleChip onClick={() => ed.flip('v')} active={!!img.flipY} title={t('DESIGN_EDITOR.FLIP_V')}><FlipVertical2 /></ToggleChip>
                    <ToggleChip onClick={() => ed.fitToArtboard(img, 'cover')} title={t('DESIGN_EDITOR.FILL_ARTBOARD')}><Maximize /></ToggleChip>
                    <ToggleChip onClick={() => ed.setAsBackground(img)} title={t('DESIGN_EDITOR.SET_BACKGROUND')}><Wallpaper /></ToggleChip>
                </div>
            </PanelSection>
            <PanelSection title={t('DESIGN_EDITOR.MASK')}>
                <div className="grid grid-cols-4 gap-1.5">
                    {(['none', 'rounded', 'circle', 'arch'] as MaskKind[]).map((k) => (
                        <ToggleChip key={k} active={mask === k} onClick={() => ed.setMask(k)} title={t(`DESIGN_EDITOR.MASK_${k.toUpperCase()}`)}>
                            <MaskGlyph kind={k} />
                        </ToggleChip>
                    ))}
                </div>
                {mask === 'rounded' && (
                    <SliderField label={t('DESIGN_EDITOR.CORNER_RADIUS')} min={0} max={Math.round(u * 0.25)} step={1} suffix=" px" value={any.csMaskRadius ?? 24} onChange={(v) => ed.setMask('rounded', v)} />
                )}
            </PanelSection>
            <PanelSection title={t('DESIGN_EDITOR.ADJUST')} defaultOpen={false}>
                <SliderField label={t('DESIGN_EDITOR.BRIGHTNESS')} min={-0.6} max={0.6} step={0.01} precision={2} value={f.brightness} onChange={(v) => ed.applyFilters({ brightness: v })} />
                <SliderField label={t('DESIGN_EDITOR.CONTRAST')} min={-0.6} max={0.6} step={0.01} precision={2} value={f.contrast} onChange={(v) => ed.applyFilters({ contrast: v })} />
                <SliderField label={t('DESIGN_EDITOR.SATURATION')} min={-1} max={1} step={0.01} precision={2} value={f.saturation} onChange={(v) => ed.applyFilters({ saturation: v })} />
                <SliderField label={t('DESIGN_EDITOR.BLUR')} min={0} max={0.6} step={0.01} precision={2} value={f.blur} onChange={(v) => ed.applyFilters({ blur: v })} />
                <ToggleChip active={f.grayscale} onClick={() => ed.applyFilters({ grayscale: !f.grayscale })}>{t('DESIGN_EDITOR.GRAYSCALE')}</ToggleChip>
            </PanelSection>
        </>
    );
};

const MaskGlyph: React.FC<{ kind: MaskKind }> = ({ kind }) => (
    <svg viewBox="0 0 20 20" className="size-4" fill="currentColor" opacity={0.75}>
        {kind === 'none' && <rect x="3" y="3" width="14" height="14" />}
        {kind === 'rounded' && <rect x="3" y="3" width="14" height="14" rx="4" />}
        {kind === 'circle' && <circle cx="10" cy="10" r="7" />}
        {kind === 'arch' && <path d="M4 18 V9 A6 6 0 0 1 16 9 V18 Z" />}
    </svg>
);

const ShadowControls: React.FC<{ ed: DesignEditorApi; obj: fabric.Object; swatches: string[] }> = ({ ed, obj, swatches }) => {
    const { t } = useTranslation();
    const sh = obj.shadow as fabric.Shadow | null | undefined;
    const on = !!sh;
    const { hex, alpha } = parseColorWithAlpha((sh?.color as string) || 'rgba(0,0,0,0.3)');
    const u = Math.min(ed.artboard.w, ed.artboard.h);
    const set = (patch: Partial<{ color: string; blur: number; offsetX: number; offsetY: number }>) => {
        ed.update({
            shadow: new fabric.Shadow({
                color: patch.color ?? rgba(hex, alpha),
                blur: patch.blur ?? sh?.blur ?? u * 0.02,
                offsetX: patch.offsetX ?? sh?.offsetX ?? 0,
                offsetY: patch.offsetY ?? sh?.offsetY ?? u * 0.006,
            } as any),
        });
    };
    return (
        <>
            <label className="flex cursor-pointer items-center justify-between text-xs text-foreground">
                <span>{t('DESIGN_EDITOR.SHADOW')}</span>
                <Switch
                    checked={on}
                    onCheckedChange={(v) => ed.update({
                        shadow: v ? new fabric.Shadow({ color: 'rgba(0, 0, 0, 0.28)', blur: u * 0.025, offsetX: 0, offsetY: u * 0.006 } as any) : null,
                    }, { history: 'now' })}
                />
            </label>
            {on && (
                <div className="flex flex-col gap-3 rounded-xl bg-foreground/[0.03] p-3">
                    <ColorField label={t('DESIGN_EDITOR.SHADOW_COLOR')} title={t('DESIGN_EDITOR.SHADOW_COLOR')} value={sh?.color as string} onChange={(p) => typeof p === 'string' && set({ color: p })} swatches={swatches} />
                    <SliderField label={t('DESIGN_EDITOR.BLUR')} min={0} max={Math.round(u * 0.1)} step={1} suffix=" px" value={sh?.blur || 0} onChange={(v) => set({ blur: v })} />
                    <div className="grid grid-cols-2 gap-3">
                        <SliderField label="X" min={-Math.round(u * 0.05)} max={Math.round(u * 0.05)} step={1} value={sh?.offsetX || 0} onChange={(v) => set({ offsetX: v })} />
                        <SliderField label="Y" min={-Math.round(u * 0.05)} max={Math.round(u * 0.05)} step={1} value={sh?.offsetY || 0} onChange={(v) => set({ offsetY: v })} />
                    </div>
                </div>
            )}
        </>
    );
};
